import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#recursos", label: "Recursos" },
    { href: "#precos", label: "Preços" },
    { href: "#comparativo", label: "Comparativo" },
    { href: "/ajuda", label: "Central de Ajuda", isRoute: true },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-card py-2 md:py-3" : "bg-transparent py-3 md:py-5"
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center min-h-[44px]" aria-label="Ir para página inicial - Zavvi.ai">
          <span className="font-display text-xl md:text-2xl font-bold text-zavvi-cyan">
            Zavvi.ai
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8" role="navigation" aria-label="Navegação principal">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-foreground/70 hover:text-foreground transition-colors font-medium text-sm py-2 min-h-[44px] flex items-center"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="text-foreground/70 hover:text-foreground transition-colors font-medium text-sm py-2 min-h-[44px] flex items-center"
              >
                {link.label}
              </a>
            )
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3 lg:gap-4">
          <a 
            href="https://zavviapp.zavvi.ai/#/login" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Fazer login na plataforma Zavvi"
          >
            <Button variant="ghost" size="sm" className="min-h-[44px]">
              Login
            </Button>
          </a>
          <Button 
            variant="light" 
            size="sm"
            className="min-h-[44px]"
            onClick={() => document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' })}
            aria-label="Ver planos e iniciar agora"
          >
            Iniciar Agora
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground p-3 min-w-[48px] min-h-[48px] flex items-center justify-center"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Fechar menu de navegação" : "Abrir menu de navegação"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden glass-card mt-2 mx-4 rounded-xl p-4"
          >
            <nav className="flex flex-col gap-1" role="navigation" aria-label="Menu de navegação mobile">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-foreground/70 hover:text-foreground transition-colors font-medium py-3 text-sm min-h-[48px] flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-foreground/70 hover:text-foreground transition-colors font-medium py-3 text-sm min-h-[48px] flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              <div className="flex flex-col gap-2 pt-3 border-t border-border">
                <a 
                  href="https://zavviapp.zavvi.ai/#/login" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Fazer login na plataforma Zavvi"
                >
                  <Button variant="ghost" size="sm" className="w-full min-h-[48px]">
                    Login
                  </Button>
                </a>
                <Button 
                  variant="light" 
                  size="sm"
                  className="min-h-[48px]"
                  onClick={() => {
                    setIsMenuOpen(false);
                    document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  aria-label="Ver planos e iniciar agora"
                >
                  Iniciar Agora
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
